import { PatientAvailabilityDto } from "../../../api-client";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import minMax from "dayjs/plugin/minMax";
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(minMax);

export type DayData = {
  uid: string;
  id: string | null;
  groupId: string;
  dayOfWeek: number;
  selected: boolean;
  startTime: string | null;
  endTime: string | null;
  availabilityStatus?: "available" | "unavailable" | "partial";
};

export type Row = {
  groupId: string;
  validityStart: string | null;
  validityEnd: string | null;
  dayData: DayData[];
};

/**
 * Parses time string into dayjs object or ISO string
 */
export const parseTime = (timeStr: string, convertToISO?: boolean) => {
  let d: dayjs.Dayjs;
  if (timeStr.includes(":")) {
    // Handle HH:MM format
    const [h, m] = timeStr.split(":").map(Number);
    d = dayjs().hour(h).minute(m).second(0).millisecond(0);
  } else {
    // Handle ISO string format
    d = dayjs(timeStr);
  }

  if (convertToISO) {
    return d.toISOString();
  } else {
    return d;
  }
};

/**
 * Validates time input format and logic
 */
export const validateTimeInput = (startTime: string | null, endTime: string | null): string | null => {
  if (!startTime || !endTime) return null;
  
  // Check if both times are in valid HH:MM format before validating
  const timeRegex = /^\d{1,2}:\d{2}$/;
  if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
    return null; // Don't validate incomplete time formats
  }
  
  const start = parseTime(startTime) as dayjs.Dayjs;
  const end = parseTime(endTime) as dayjs.Dayjs;
  if (start.isSame(end) || start.isAfter(end)) {
    return "Endzeit muss nach der Startzeit liegen.";
  }
  
  return null;
};

/**
 * Validates date range - checks that start date is before end date
 */
export const validateDateRange = (startDate: string | null, endDate: string | null): string | null => {
  if (!startDate || !endDate) return null;

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return null; // Don't validate if dates are invalid format
  }

  if (start.isSame(end) || start.isAfter(end)) {
    return "Enddatum muss nach dem Startdatum liegen.";
  }

  return null;
};

/**
 * Formats date for MobiliDate component (ISO string format)
 */
export const formatDateForMobiliDate = (date: Date | string | null): string => {
  if (!date) return "";

  // If it's already a string (ISO format), return just the date part
  if (typeof date === "string") {
    return dayjs(date).format("YYYY-MM-DD");
  }

  // If it's a Date object, convert to ISO string and return date part
  if (date instanceof Date) {
    return dayjs(date).format("YYYY-MM-DD");
  }

  return "";
};

/**
 * Format dates for display (DD.MM.YYYY)
 */
export const formatDateForDisplay = (date: Date | string | null): string => {
  if (!date) return "";
  return dayjs(date).format("DD.MM.YYYY");
};

/**
 * Determines availability status from day data
 */
export const getAvailabilityStatus = (dayData: any): "available" | "unavailable" | "partial" => {
  if (!dayData?.selected) return "available";

  if (dayData.availabilityStatus) return dayData.availabilityStatus;
  if (dayData.startTime && dayData.endTime) return "partial";
  return "unavailable";
};

/**
 * Converts availability time blocks to unavailability blocks when toggle is set to "Verfügbar"
 */
export const transformAvailabilityToUnavailability = (
  dayBlocks: DayData[],
  dayOfWeek: number,
  groupId: string,
  validityStart: string | null,
  validityEnd: string | null
): PatientAvailabilityDto[] => {
  // sort time blocks
  const sorted = [...dayBlocks].sort((a, b) => dayjs(a.startTime!).valueOf() - dayjs(b.startTime!).valueOf());

  // merge overlapping periods
  const merged: DayData[] = [];
  for (const block of sorted) {
    if (merged.length === 0) {
      merged.push({ ...block });
    } else {
      const last = merged[merged.length - 1];
      if (dayjs(block.startTime!).isSameOrBefore(dayjs(last.endTime!))) {
        const maxTime = dayjs.max(dayjs(block.endTime!), dayjs(last.endTime!));
        last.endTime = maxTime ? maxTime.toISOString() : dayjs(block.endTime!).toISOString();
      } else {
        merged.push({ ...block });
      }
    }
  }

  // convert to unavailabilities
  const startOfDay = dayjs().hour(7).minute(0).second(0).millisecond(0);
  const endOfDay = dayjs().hour(20).minute(0).second(0).millisecond(0);

  const unavailabilities: PatientAvailabilityDto[] = [];

  let prevEnd = startOfDay;
  for (const block of merged) {
    const blockStart = dayjs(block.startTime!);
    if (prevEnd.isBefore(blockStart)) {
      unavailabilities.push({
        groupId,
        dayOfWeek,
        startTime: prevEnd.toISOString(),
        endTime: blockStart.toISOString(),
        validityStart: validityStart ? dayjs(validityStart).toISOString() : null,
        validityEnd: validityEnd ? dayjs(validityEnd).toISOString() : null,
      } as any);
    }
    const maxTime = dayjs.max(prevEnd, dayjs(block.endTime!));
    prevEnd = maxTime || dayjs(block.endTime!);
  }

  if (prevEnd.isBefore(endOfDay)) {
    unavailabilities.push({
      groupId,
      dayOfWeek,
      startTime: prevEnd.toISOString(),
      endTime: endOfDay.toISOString(),
      validityStart: validityStart ? dayjs(validityStart).toISOString() : null,
      validityEnd: validityEnd ? dayjs(validityEnd).toISOString() : null,
    } as any);
  }

  return unavailabilities;
};

/**
 * Checks for overlaps with time blocks (validation only)
 */
export const checkTimeBlockOverlap = (startTime: string | null, endTime: string | null, dayBlocks: DayData[]): { hasOverlap: boolean, overlappingBlocks: DayData[], message?: string } => {
  if (!startTime || !endTime) {
    return { hasOverlap: false, overlappingBlocks: [] };
  }

  const timeRegex = /^\d{1,2}:\d{2}$/;
  if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
    return { hasOverlap: false, overlappingBlocks: [] };
  }
  
  // Parse input times with consistent date handling
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  const baseDate = dayjs().startOf('day');
  const inputStart = baseDate.hour(startHour).minute(startMin);
  const inputEnd = baseDate.hour(endHour).minute(endMin);

  // Filter out blocks without time data for validation
  const validBlocks = dayBlocks.filter(block => block.startTime && block.endTime);
  
  // Check for overlaps and return info
  const overlappingBlocks = validBlocks.filter((block) => {
    // Parse existing block times - they are stored as ISO strings
    const blockStartDayjs = dayjs(block.startTime!);
    const blockEndDayjs = dayjs(block.endTime!);
    
    // Extract just the time part and apply to the same base date
    const blockStart = baseDate.hour(blockStartDayjs.hour()).minute(blockStartDayjs.minute());
    const blockEnd = baseDate.hour(blockEndDayjs.hour()).minute(blockEndDayjs.minute());
    
    // Check if there's an overlap (inclusive comparison)
    return inputEnd.isSameOrAfter(blockStart) && inputStart.isSameOrBefore(blockEnd);
  });
  
  if (overlappingBlocks.length > 0) {
    const overlappingTimes = overlappingBlocks.map(
      (block) => {
        const blockStart = dayjs(block.startTime!);
        const blockEnd = dayjs(block.endTime!);
        const startFormatted = blockStart.format('HH:mm');
        const endFormatted = blockEnd.format('HH:mm');
        return `${startFormatted}-${endFormatted}`;
      }
    ).join(', ');
    
    return {
      hasOverlap: true,
      overlappingBlocks,
      message: `Überschneidung mit bestehenden Zeiträumen: ${overlappingTimes}`
    };
  }
  
  return { hasOverlap: false, overlappingBlocks: [] };
};

/**
 * Merges overlapping time blocks with a new time block
 */
export const mergeTimeBlocks = (startTime: string, endTime: string, existingBlocks: DayData[], groupId: string, dayOfWeek: number): DayData[] => {
  // Parse input times with consistent date handling (input is in HH:MM format)
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  const baseDate = dayjs().startOf('day');
  const inputStart = baseDate.hour(startHour).minute(startMin);
  const inputEnd = baseDate.hour(endHour).minute(endMin);

  // Filter out blocks without time data
  const validBlocks = existingBlocks.filter(block => block.startTime && block.endTime);
  
  // Merge logic: expand the range to include overlapping blocks
  let mergedStart = inputStart;
  let mergedEnd = inputEnd;

  const nonOverlapping: DayData[] = [];

  for (const block of validBlocks) {
    // Parse existing block times - they are stored as ISO strings
    const blockStartDayjs = dayjs(block.startTime!);
    const blockEndDayjs = dayjs(block.endTime!);
    
    // Extract just the time part and apply to the same base date for consistent comparison
    const blockStart = baseDate.hour(blockStartDayjs.hour()).minute(blockStartDayjs.minute());
    const blockEnd = baseDate.hour(blockEndDayjs.hour()).minute(blockEndDayjs.minute());

    if (mergedEnd.isSameOrAfter(blockStart) && mergedStart.isSameOrBefore(blockEnd)) {
      // overlap → expand merged range
      const minTime = dayjs.min(mergedStart, blockStart);
      const maxTime = dayjs.max(mergedEnd, blockEnd);
      mergedStart = minTime || mergedStart;
      mergedEnd = maxTime || mergedEnd;
    } else {
      // no overlap → keep the block
      nonOverlapping.push(block);
    }
  }

  // add merged block
  nonOverlapping.push({
    uid: "",
    id: null,
    groupId,
    dayOfWeek,
    selected: true,
    startTime: mergedStart.toISOString(),
    endTime: mergedEnd.toISOString(),
    availabilityStatus: "partial" as const
  });
  
  // sort by start time for consistency
  return nonOverlapping.sort((a, b) => dayjs(a.startTime!).valueOf() - dayjs(b.startTime!).valueOf());
};

/**
 * Generate the tooltip text to show unavailable time slots for availabilityStatus = "partial"
 */
export const generateUnavailableTimeBlocksTooltip = (dayBlocks: DayData[]): string => {
  const unavailableSlots = dayBlocks
    .filter(block => block.startTime && block.endTime)
    .map(block => {
      const start = dayjs(block.startTime!).format("HH:mm");
      const end = dayjs(block.endTime!).format("HH:mm");
      return `${start} - ${end}`;
    });
  const sorted = unavailableSlots.sort();
  return `Unverfügbare Zeiträume: ${sorted.join(", ")}`;
};
